import type { Mission } from '../mission/schema';
import type { MissionCatalogue } from '../mission/MissionLoader';
import type { DebriefReport } from '../telemetry/Debrief';
import type { Save, Settings } from '../save/Save';
import type { Terrain } from '../world/Terrain';
import { TheatreMap, frameFor } from './TheatreMap';
import { buildSettingsPanel } from './SettingsPanel';

/**
 * The menu shell (SPEC §3.14).
 *
 * ```
 * BOOT → MAIN MENU ─┬─ CAMPAIGN → mission select → BRIEF → LOADOUT → FLY → DEBRIEF ─┐
 *                   ├─ FREE FLIGHT → FLY ───────────────────────────────────────────┤
 *                   ├─ SETTINGS / CONTROLS                     ←───────────────────┘
 * ```
 *
 * DOM and CSS rather than Canvas, per SPEC §4.1: "menus are documents; the HUD is a 60 fps vector
 * display". Building menus in the render loop would be a category error — they do not animate, do
 * not need the GPU, and get accessibility, text selection and reflow for free from the browser.
 *
 * The M10 acceptance criterion is that "a cold visitor can go from load to completing mission 1
 * without external instruction", so the flow is deliberately shallow: two clicks from boot to
 * flying, and the brief carries the instructions the tutorial would otherwise have to narrate.
 */

export const Screen = {
  Main: 'main',
  MissionSelect: 'missions',
  Brief: 'brief',
  Settings: 'settings',
  Debrief: 'debrief',
  Paused: 'paused',
  Flying: 'flying',
} as const;
export type ScreenId = (typeof Screen)[keyof typeof Screen];

export interface ShellCallbacks {
  onFly(mission: Mission): void;
  onFreeFlight(): void;
  onSettingsChanged(settings: Settings): void;
  onReplay(): void;
  /** Leave the pause menu and hand the simulation back. */
  onResume(): void;
  /** Fly the current mission again from its launch point. */
  onRestart(): void;
  /** Give up the current mission: no debrief, no score. */
  onAbandon(): void;
}

export class Shell {
  readonly root: HTMLDivElement;

  private screen: ScreenId = Screen.Main;
  private readonly catalogue: MissionCatalogue;
  private readonly save: Save;
  private readonly callbacks: ShellCallbacks;
  private readonly map: TheatreMap;
  private terrain: Terrain | null = null;
  private selected: Mission | null = null;
  /**
   * Where SETTINGS came from, so its BACK goes back.
   *
   * Settings is reachable from the main menu and from the pause menu, and a pause menu that
   * returned you to the main menu — abandoning the mission in flight — would be a trapdoor.
   */
  private settingsReturn: ScreenId = Screen.Main;

  constructor(catalogue: MissionCatalogue, save: Save, callbacks: ShellCallbacks) {
    this.catalogue = catalogue;
    this.save = save;
    this.callbacks = callbacks;
    this.map = new TheatreMap();

    this.root = document.createElement('div');
    this.root.className = 'vz-shell';
    document.body.appendChild(this.root);
    injectStyles();
    this.render();
  }

  setTerrain(terrain: Terrain): void {
    this.terrain = terrain;
  }

  get currentScreen(): ScreenId {
    return this.screen;
  }

  get visible(): boolean {
    return this.screen !== Screen.Flying;
  }

  show(screen: ScreenId): void {
    this.screen = screen;
    this.render();
  }

  /** Hand control to the simulation. The shell hides itself rather than unmounting. */
  hide(): void {
    this.screen = Screen.Flying;
    this.render();
  }

  showDebrief(report: DebriefReport, track: Float32Array): void {
    this.screen = Screen.Debrief;
    this.render(report, track);
  }

  private render(report?: DebriefReport, track?: Float32Array): void {
    this.root.replaceChildren();
    this.root.style.display = this.screen === Screen.Flying ? 'none' : 'grid';
    // The pause menu is the one screen drawn over a picture the player wants to keep seeing: it
    // dims the frozen frame rather than replacing it, so the aircraft is still visibly *there*.
    this.root.classList.toggle('vz-shell--over-world', this.screen === Screen.Paused);
    if (this.screen === Screen.Flying) return;

    switch (this.screen) {
      case Screen.Main:
        this.renderMain();
        break;
      case Screen.MissionSelect:
        this.renderMissionSelect();
        break;
      case Screen.Brief:
        this.renderBrief();
        break;
      case Screen.Settings:
        this.renderSettings();
        break;
      case Screen.Debrief:
        if (report !== undefined) this.renderDebrief(report, track);
        break;
      case Screen.Paused:
        this.renderPaused();
        break;
      default:
        break;
    }
  }

  private panel(title: string): HTMLDivElement {
    const panel = document.createElement('div');
    panel.className = 'vz-panel';
    const heading = document.createElement('h1');
    heading.textContent = title;
    panel.appendChild(heading);
    this.root.appendChild(panel);
    return panel;
  }

  private button(parent: HTMLElement, label: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'vz-button';
    button.textContent = label;
    button.addEventListener('click', onClick);
    parent.appendChild(button);
    return button;
  }

  private renderMain(): void {
    const panel = this.panel('VECTOR ZERO');
    const tagline = document.createElement('p');
    tagline.className = 'vz-tagline';
    tagline.textContent =
      'A one-way attack drone over the Kerani coast. The aircraft is the weapon; there is no way home.';
    panel.appendChild(tagline);

    this.button(panel, 'CAMPAIGN', () => this.show(Screen.MissionSelect));
    this.button(panel, 'FREE FLIGHT', () => {
      this.callbacks.onFreeFlight();
      this.hide();
    });
    this.button(panel, 'SETTINGS', () => {
      this.settingsReturn = Screen.Main;
      this.show(Screen.Settings);
    });

    if (this.catalogue.broken.length > 0) {
      const warning = document.createElement('p');
      warning.className = 'vz-warning';
      warning.textContent = `${this.catalogue.broken.length} mission file(s) failed to load — see the console.`;
      panel.appendChild(warning);
    }
  }

  private renderMissionSelect(): void {
    const panel = this.panel('CAMPAIGN');
    const list = document.createElement('div');
    list.className = 'vz-list';

    for (let i = 0; i < this.catalogue.campaign.length; i += 1) {
      const mission = this.catalogue.campaign[i];
      const record = this.save.campaign[mission.id];
      const row = document.createElement('button');
      row.className = 'vz-row';
      // Built as nodes rather than markup: mission text is authored data, but assembling the DOM
      // directly removes the escaping question rather than answering it.
      row.append(
        span('vz-num', String(mission.order).padStart(2, '0')),
        span('vz-title', mission.title),
        span('vz-rank', record === undefined ? '—' : record.rank),
      );
      row.addEventListener('click', () => {
        this.selected = mission;
        this.show(Screen.Brief);
      });
      list.appendChild(row);
    }
    panel.appendChild(list);
    this.button(panel, 'BACK', () => this.show(Screen.Main));
  }

  private renderBrief(): void {
    const mission = this.selected;
    if (mission === null) {
      this.show(Screen.MissionSelect);
      return;
    }
    const panel = this.panel(`${String(mission.order).padStart(2, '0')} · ${mission.title}`);
    panel.classList.add('vz-brief');

    const columns = document.createElement('div');
    columns.className = 'vz-columns';

    const left = document.createElement('div');
    const summary = document.createElement('p');
    summary.className = 'vz-summary';
    summary.textContent = mission.brief.summary;
    left.appendChild(summary);

    left.appendChild(sectionList('INTELLIGENCE', mission.brief.intel));
    if (mission.brief.rulesOfEngagement !== undefined) {
      left.appendChild(sectionList('RULES OF ENGAGEMENT', mission.brief.rulesOfEngagement));
    }
    left.appendChild(
      sectionList(
        'TASKING',
        mission.objectives.map(
          (objective) => `${objective.primary ? '[P]' : '[S]'} ${objective.description}`,
        ),
      ),
    );
    left.appendChild(
      sectionList('LOADOUT', [
        `Warhead: ${mission.loadout.warhead}`,
        `Chaff: ${mission.loadout.chaff}`,
        `Flares: ${mission.loadout.flares}`,
      ]),
    );

    const right = document.createElement('div');
    right.className = 'vz-mapwrap';
    this.map.setView(frameFor(mission));
    if (this.terrain !== null) this.map.shadeTerrain(this.terrain);
    this.map.drawBriefing(mission);
    right.appendChild(this.map.canvas);
    const legend = document.createElement('div');
    legend.className = 'vz-legend';
    legend.append(
      span('vz-k vz-k-search', 'dashed'),
      document.createTextNode(' search radius · '),
      span('vz-k vz-k-engage', 'solid'),
      document.createTextNode(' engagement radius · '),
      span('vz-k vz-k-route', 'cyan'),
      document.createTextNode(' route · '),
      span('vz-k vz-k-target', 'amber'),
      document.createTextNode(' target'),
    );
    right.appendChild(legend);

    columns.append(left, right);
    panel.appendChild(columns);

    const actions = document.createElement('div');
    actions.className = 'vz-actions';
    panel.appendChild(actions);
    this.button(actions, 'FLY', () => {
      this.callbacks.onFly(mission);
      this.hide();
    });
    this.button(actions, 'BACK', () => this.show(Screen.MissionSelect));
  }

  private renderSettings(): void {
    const panel = this.panel('SETTINGS');
    panel.appendChild(
      buildSettingsPanel(this.save.settings, () => {
        this.save.save();
        this.callbacks.onSettingsChanged(this.save.settings);
      }),
    );
    this.button(panel, 'BACK', () => this.show(this.settingsReturn));
  }

  /**
   * The pause menu (SPEC §3.13's `Escape` binding, unwired until now).
   *
   * Deliberately four lines and no information: the player paused to stop, not to read. Everything
   * the mission has to say is already on the HUD behind this panel, which is why the screen dims
   * the picture rather than covering it.
   */
  private renderPaused(): void {
    const panel = this.panel('PAUSED');
    this.button(panel, 'RESUME', () => this.callbacks.onResume());
    this.button(panel, 'RESTART MISSION', () => this.callbacks.onRestart());
    this.button(panel, 'SETTINGS', () => {
      this.settingsReturn = Screen.Paused;
      this.show(Screen.Settings);
    });
    this.button(panel, 'ABANDON MISSION', () => this.callbacks.onAbandon());
  }

  private renderDebrief(report: DebriefReport, track?: Float32Array): void {
    const panel = this.panel(`DEBRIEF · ${report.missionTitle}`);
    panel.classList.add('vz-brief');

    const verdict = document.createElement('p');
    verdict.className = report.success ? 'vz-verdict vz-ok' : 'vz-verdict vz-fail';
    verdict.textContent = `${report.success ? 'MISSION COMPLETE' : 'MISSION FAILED'} — ${report.outcomeReason}`;
    panel.appendChild(verdict);

    const columns = document.createElement('div');
    columns.className = 'vz-columns';
    const left = document.createElement('div');

    if (report.score !== null) {
      const score = report.score;
      left.appendChild(
        sectionList('SCORE', [
          `Total: ${score.total}`,
          `Rank: ${score.rank}`,
          ...score.breakdown.map(
            (b) => `${b.component}: ${Math.round(b.points)} / ${b.weight} (${Math.round(b.normalised * 100)}%)`,
          ),
        ]),
      );
    }

    left.appendChild(
      sectionList(
        'OBJECTIVES',
        report.objectives.map(
          (objective) =>
            `${objective.state === 'complete' ? '+' : objective.state === 'failed' ? '×' : '·'} ${objective.description}`,
        ),
      ),
    );

    // The engagement analysis: the reason the recorder exists (SPEC §3.6.5).
    if (report.missiles.length > 0) {
      const section = document.createElement('div');
      section.className = 'vz-section';
      const heading = document.createElement('h2');
      heading.textContent = 'ENGAGEMENTS';
      section.appendChild(heading);
      for (let i = 0; i < report.missiles.length; i += 1) {
        const missile = report.missiles[i];
        const entry = document.createElement('div');
        entry.className = `vz-engagement vz-${missile.verdict.toLowerCase()}`;
        const headline = document.createElement('strong');
        headline.textContent =
          `${missile.designation} · ${missile.verdict} · ${Math.round(missile.missDistanceM)} m`;
        entry.append(headline, document.createElement('br'));
        entry.appendChild(document.createTextNode(missile.explanation));
        section.appendChild(entry);
      }
      left.appendChild(section);
    }

    if (report.lessons.length > 0) left.appendChild(sectionList('TAKEAWAYS', report.lessons));

    const right = document.createElement('div');
    right.className = 'vz-mapwrap';
    if (this.selected !== null) this.map.setView(frameFor(this.selected));
    if (this.terrain !== null) this.map.shadeTerrain(this.terrain);
    if (this.selected !== null) this.map.drawBriefing(this.selected);
    if (track !== undefined) this.map.drawTrack(track);
    right.appendChild(this.map.canvas);
    right.appendChild(
      sectionList('FLIGHT', [
        `Time: ${report.elapsedS.toFixed(0)} s`,
        `Mean ingress altitude: ${Math.round(report.meanIngressAltitudeM)} m AGL`,
        `Peak load factor: ${report.peakLoadFactor.toFixed(1)} g`,
      ]),
    );

    columns.append(left, right);
    panel.appendChild(columns);

    const actions = document.createElement('div');
    actions.className = 'vz-actions';
    panel.appendChild(actions);
    this.button(actions, 'REPLAY', () => this.callbacks.onReplay());
    this.button(actions, 'MISSION SELECT', () => this.show(Screen.MissionSelect));
    this.button(actions, 'MAIN MENU', () => this.show(Screen.Main));
  }
}

function sectionList(title: string, lines: readonly string[]): HTMLDivElement {
  const section = document.createElement('div');
  section.className = 'vz-section';
  const heading = document.createElement('h2');
  heading.textContent = title;
  section.appendChild(heading);
  const list = document.createElement('ul');
  for (let i = 0; i < lines.length; i += 1) {
    const item = document.createElement('li');
    item.textContent = lines[i];
    list.appendChild(item);
  }
  section.appendChild(list);
  return section;
}

function span(className: string, text: string): HTMLSpanElement {
  const element = document.createElement('span');
  element.className = className;
  element.textContent = text;
  return element;
}

let stylesInjected = false;

/** Styles live here rather than in a .css file so the shell stays one self-contained module. */
function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = `
.vz-shell {
  position: fixed; inset: 0; z-index: 40; display: grid; place-items: center;
  background: radial-gradient(circle at 50% 40%, #0d1620 0%, #05080c 70%);
  color: #cfe9e6; font: 13px/1.55 "SF Mono", "Roboto Mono", Menlo, Consolas, ui-monospace, monospace;
  overflow: auto; padding: 24px; text-transform: uppercase; letter-spacing: 0.04em;
}
/* Pause: dim the frozen frame instead of replacing it. Dark enough that the menu reads, light
   enough that the aircraft, the terrain and the HUD are all still legible behind it. */
.vz-shell--over-world { background: rgba(3, 7, 10, 0.72); }
.vz-shell--over-world .vz-panel { width: min(420px, 90vw); }
.vz-panel { width: min(1180px, 96vw); }
.vz-panel h1 { font-size: 22px; letter-spacing: 0.3em; color: #3fe8d8; margin: 0 0 18px; font-weight: 400; }
.vz-panel h2 { font-size: 11px; letter-spacing: 0.24em; color: #7fb5b0; margin: 18px 0 6px; font-weight: 400; }
.vz-tagline, .vz-summary { color: #9fc0bd; text-transform: none; letter-spacing: 0; max-width: 60ch; }
.vz-button {
  display: block; width: 260px; margin: 8px 0; padding: 11px 16px; text-align: left;
  background: transparent; color: #3fe8d8; border: 1px solid rgba(63,232,216,0.4);
  font: inherit; letter-spacing: 0.18em; cursor: pointer;
}
.vz-button:hover { background: rgba(63,232,216,0.1); border-color: #3fe8d8; }
.vz-list { margin: 12px 0; }
.vz-row {
  display: grid; grid-template-columns: 40px 1fr 110px; width: 100%; gap: 12px; padding: 9px 12px;
  background: transparent; border: 1px solid rgba(63,232,216,0.16); color: #cfe9e6;
  font: inherit; text-align: left; cursor: pointer; margin-bottom: 4px; letter-spacing: 0.1em;
}
.vz-row:hover { border-color: rgba(63,232,216,0.6); background: rgba(63,232,216,0.06); }
.vz-num { color: #5e8a86; }
.vz-rank { color: #ffd666; text-align: right; }
.vz-columns { display: grid; grid-template-columns: 1fr auto; gap: 28px; align-items: start; }
.vz-mapwrap { display: grid; gap: 8px; }
.vz-map { border: 1px solid rgba(63,232,216,0.25); display: block; }
.vz-legend { font-size: 10px; color: #7fb5b0; text-transform: none; letter-spacing: 0; }
.vz-k-search { color: rgba(255,138,96,0.95); }
.vz-k-engage { color: rgba(255,92,74,0.95); }
.vz-k-route { color: #3fe8d8; }
.vz-k-target { color: #ffd666; }
.vz-section ul { margin: 0; padding-left: 16px; }
.vz-section li { color: #9fc0bd; text-transform: none; letter-spacing: 0; margin-bottom: 3px; }
.vz-actions { display: flex; gap: 12px; margin-top: 22px; }
.vz-actions .vz-button { width: auto; min-width: 150px; margin: 0; }
.vz-verdict { letter-spacing: 0.18em; }
.vz-ok { color: #3fe8d8; }
.vz-fail { color: #ff5c4a; }
.vz-warning { color: #ff8a60; }
.vz-engagement {
  border-left: 2px solid rgba(63,232,216,0.4); padding: 6px 10px; margin: 6px 0;
  color: #9fc0bd; text-transform: none; letter-spacing: 0; max-width: 62ch;
}
.vz-engagement strong { color: #cfe9e6; letter-spacing: 0.08em; text-transform: uppercase; }
.vz-hit { border-left-color: #ff5c4a; }
.vz-clean { border-left-color: #3fe8d8; }
.vz-incidental { border-left-color: #ffd666; }
.vz-setting { display: grid; grid-template-columns: 260px 1fr 70px; gap: 12px; align-items: center; margin: 5px 0; }
.vz-setting label { color: #9fc0bd; text-transform: none; letter-spacing: 0; }
.vz-setting output { color: #3fe8d8; text-align: right; }
.vz-group { border: 1px solid rgba(63,232,216,0.14); padding: 12px 16px; margin: 12px 0; }
`;
  document.head.appendChild(style);
}
